import PatientFormModal from "./PatientFormModal";
import { PatientFormData } from "../utils/patientValidation";

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (customerData: PatientFormData) => Promise<void>;
  initialData: {
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
    dateOfBirth: string | null;
    ssnLast4: string | null;
    address: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
  };
}

const validateAddressForEdit = (address: string): string => {
  if (!address?.trim()) return "Address is required";

  const trimmed = address.trim();
  if (trimmed.length < 5) return "Please enter a complete US address";
  if (trimmed.length > 200) return "Address is too long";

  const firstSpaceIdx = trimmed.indexOf(" ");
  if (firstSpaceIdx === -1) {
    return 'Please include a street name after the house number (e.g., "123 Main St")';
  }

  const houseNumber = trimmed.slice(0, firstSpaceIdx);
  const remainder = trimmed.slice(firstSpaceIdx + 1).trim();

  if (!/^\d{1,6}$/.test(houseNumber)) {
    return "Please start the address with a valid house number (1–6 digits)";
  }

  if (remainder.length < 3) return "Please enter a complete street name";
  if (remainder.length > 190) return "Street name is too long";
  if (!/^[A-Za-z0-9\s.,#\-']+$/.test(remainder)) {
    return "Please enter a valid US address format (allowed characters: letters, digits, spaces, . , # - ' )";
  }

  return "";
};

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  initialData,
}) => {
  // Convert null values to undefined for Partial<PatientFormData>
  const normalizedInitialData: Partial<PatientFormData> = {
    firstName: initialData.firstName ?? undefined,
    lastName: initialData.lastName ?? undefined,
    phoneNumber: initialData.phoneNumber ?? undefined,
    dateOfBirth: initialData.dateOfBirth ?? undefined,
    ssnLast4: initialData.ssnLast4 ?? undefined,
    address: initialData.address ?? undefined,
    addressLine2: initialData.addressLine2 ?? undefined,
    city: initialData.city ?? undefined,
    state: initialData.state ?? undefined,
    zip: initialData.zip ?? undefined,
  };

  return (
    <PatientFormModal
      isOpen={isOpen}
      title="Edit Patient Details"
      subtitle="Update patient information"
      primaryLabel="Update Patient Details"
      loadingLabel="Updating..."
      onClose={onClose}
      onSubmit={onUpdate}
      validateAddress={validateAddressForEdit}
      initialData={normalizedInitialData}
      defaultErrorMessage="Failed to update patient details. Please try again."
      buildConflictMessage={(data, error) =>
        error?.status === 409
          ? `A customer with phone number ${data.phoneNumber} already exists. Please use a different phone number.`
          : null
      }
    />
  );
};

export default EditCustomerModal;

